import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarCheck } from "lucide-react";

import API from "../api";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const parseDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (s, n) => {
  const d = parseDate(s);
  d.setDate(d.getDate() + n);
  return fmtDate(d);
};

const DAY_STYLES = {
  available: "text-ivory/80 hover:bg-gold/25 cursor-pointer",
  booked: "bg-navy/80 text-ivory/35 cursor-not-allowed",
  provisional: "bg-gold/20 text-gold-2 cursor-not-allowed",
  maintenance: "bg-red-900/40 text-ivory/40 cursor-not-allowed",
  selected: "bg-gold text-navy font-semibold",
};
const STATUS_LABELS = {
  available: "Available",
  booked: "Booked — confirmed",
  provisional: "Provisional hold",
  maintenance: "Closed for maintenance",
};

export function useAvailability() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/availability`)
      .then((r) => r.json())
      .then((d) => alive && setData(d))
      .catch(() => alive && setData({ status: "unavailable", months: [] }));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

function MonthCard({ month, selectable = false, arrival, departure, onDay }) {
  const first = month.days[0];
  const firstWeekday = parseDate(first.date).getDay(); // 0=Sun
  const offset = (firstWeekday + 6) % 7; // Monday-first

  const dayClass = (day) => {
    if (selectable && (day.date === arrival || day.date === departure)) return DAY_STYLES.selected;
    return DAY_STYLES[day.status] || DAY_STYLES.available;
  };
  const inRange = (date) => selectable && arrival && departure && date > arrival && date < departure;

  return (
    <div className="border border-ivory/15 bg-navy-2 p-4" data-testid={`availability-month-${month.key}`}>
      <p className="font-display text-lg text-ivory">{month.label}</p>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest2 text-ivory/40">
        {WEEKDAYS.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1 text-xs">
        {Array.from({ length: offset }).map((_, i) => (
          <span key={`pad-${i}`} />
        ))}
        {month.days.map((day) => (
          <button
            key={day.date}
            type="button"
            disabled={!selectable || (day.status !== "available" && day.date !== arrival && day.date !== departure)}
            onClick={() => onDay && onDay(day.date, day.status)}
            title={STATUS_LABELS[day.status] || "Available"}
            data-testid={`availability-day-${day.date}`}
            className={`aspect-square leading-[1.9] transition-colors duration-200 ${dayClass(day)} ${inRange(day.date) ? "ring-1 ring-inset ring-gold/40" : ""}`}
          >
            {Number(day.date.slice(8))}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Legend() {
  return (
    <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-widest2" data-testid="availability-legend">
      <span className="flex items-center gap-2 text-ivory/70"><span className="h-3 w-3 border border-ivory/40" /> Available</span>
      <span className="flex items-center gap-2 text-ivory/70"><span className="h-3 w-3 bg-navy/80 border border-ivory/30" /> Booked</span>
      <span className="flex items-center gap-2 text-ivory/70"><span className="h-3 w-3 bg-gold/20 border border-gold/50" /> Provisional</span>
      <span className="flex items-center gap-2 text-ivory/70"><span className="h-3 w-3 bg-red-900/40 border border-ivory/30" /> Maintenance</span>
    </div>
  );
}

export function AvailabilityNotice({ data }) {
  if (!data || data.status === "ok") return null;
  return (
    <div className="mt-8 flex items-start gap-3 border border-gold/40 bg-navy-2 p-5" data-testid="availability-notice">
      {data.status === "stale" ? (
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
      ) : (
        <CalendarCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
      )}
      <p className="text-sm font-light leading-relaxed text-ivory/80" data-testid="availability-notice-text">
        {data.message ||
          "Our live availability calendar is being set up — enquire with your dates and we will confirm personally."}
      </p>
    </div>
  );
}

/** Read-only public calendar. */
export function AvailabilityCalendar({ months = 12 }) {
  const data = useAvailability();
  if (!data) {
    return <p className="mt-10 text-center text-xs uppercase tracking-widest2 text-ivory/50" data-testid="availability-loading">Checking the island calendar…</p>;
  }
  const shown = (data.months || []).slice(0, months);
  return (
    <div>
      <AvailabilityNotice data={data} />
      {shown.length > 0 && <Legend />}
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="availability-grid">
        {shown.map((m) => (
          <MonthCard key={m.key} month={m} />
        ))}
      </div>
      {data.status === "stale" && (
        <p className="mt-6 text-xs font-light text-ivory/50">
          Showing the last verified snapshot — dates shown as available are not guaranteed until confirmed.
        </p>
      )}
      {data.status === "unconfigured" && (
        <p className="mt-6 text-xs font-light text-ivory/50">
          Every day currently shows as available while the booking calendar is being connected — enquire to confirm.
        </p>
      )}
    </div>
  );
}

/** Selectable calendar used by the Build Your Own Island Weekend configurator. */
export function MonthPicker({ maxNights = 14, arrival, departure, onArrival, onDeparture }) {
  const data = useAvailability();
  const live = data && data.status === "ok";
  const statusMap = useMemo(() => {
    const map = {};
    (data?.months || []).forEach((m) => m.days.forEach((d) => (map[d.date] = d.status)));
    return map;
  }, [data]);

  const statusOf = (date) => (live ? statusMap[date] || "available" : "available");
  const nightsBetween = (a, b) => {
    const nights = [];
    let cur = a;
    while (cur < b && nights.length <= maxNights) {
      nights.push(cur);
      cur = addDays(cur, 1);
    }
    return nights;
  };
  const departureOk = (date) => {
    if (!arrival || date <= arrival) return false;
    const nights = nightsBetween(arrival, date);
    return nights.length <= maxNights && nights.every((n) => statusOf(n) === "available");
  };

  const onDay = (date) => {
    if (!arrival || (arrival && departure)) {
      onArrival(date);
      onDeparture(null);
    } else if (departureOk(date)) {
      onDeparture(date);
    } else if (date === arrival) {
      onArrival(null);
      onDeparture(null);
    } else if (statusOf(date) === "available") {
      onArrival(date);
      onDeparture(null);
    }
  };

  if (!data) {
    return <p className="mt-10 text-center text-xs uppercase tracking-widest2 text-ivory/50">Loading the island calendar…</p>;
  }
  return (
    <div>
      <AvailabilityNotice data={data} />
      <div className="mt-6 grid gap-5 sm:grid-cols-2" data-testid="month-picker">
        {(data.months || []).slice(0, 12).map((m) => (
          <MonthCard key={m.key} month={m} selectable arrival={arrival} departure={departure} onDay={onDay} />
        ))}
      </div>
      {arrival && !departure && (
        <p className="mt-5 text-sm text-gold-2" data-testid="picker-departure-hint">
          Arrival {arrival} — now choose your departure day (1–{maxNights} nights).
        </p>
      )}
      {arrival && departure && (
        <p className="mt-5 text-sm text-gold-2" data-testid="picker-selection-line">
          {arrival} → {departure} · {nightsBetween(arrival, departure).length} night{nightsBetween(arrival, departure).length === 1 ? "" : "s"} — tap another arrival date to change.
        </p>
      )}
      {live && (
        <p className="mt-4 text-xs font-light text-ivory/50">
          Dates update automatically from the lodge booking calendar. Departure days stay bookable for the next group&rsquo;s arrival.
        </p>
      )}
    </div>
  );
}